import React, { useState, useEffect } from 'react';
import {
  getAllNotificationsAdmin,
  createNotification,
  updateNotification,
  deleteNotification,
  sendPushNotification
} from '../../services/api';
import {
  Bell,
  Plus,
  Edit2,
  Trash2,
  X,
  AlertCircle,
  Send,
  Users,
  Eye,
  Smartphone
} from 'lucide-react';
import './NotificationPanel.css';

const NotificationPanel = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showPushModal, setShowPushModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editingNotification, setEditingNotification] = useState(null);
  const [deletingNotification, setDeletingNotification] = useState(null);
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  const [sendingPush, setSendingPush] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    message: '',
    type: 'announcement',
    targetAudience: 'all',
    icon: '🔔',
    actionUrl: '',
    priority: 'medium',
    expiresAt: ''
  });

  const [formErrors, setFormErrors] = useState({});

  const [pushFormData, setPushFormData] = useState({
    title: '',
    message: '',
    actionUrl: '',
    targetAudience: 'all',
    icon: '📱'
  });

  const [pushFormErrors, setPushFormErrors] = useState({});

  const notificationTypes = [
    { value: 'announcement', label: 'Announcement', color: '#2196F3' },
    { value: 'alert', label: 'Alert', color: '#f44336' },
    { value: 'update', label: 'Update', color: '#4CAF50' },
    { value: 'info', label: 'Info', color: '#00bcd4' },
    { value: 'warning', label: 'Warning', color: '#ff9800' },
    { value: 'success', label: 'Success', color: '#8bc34a' }
  ];

  const targetAudiences = [
    { value: 'all', label: 'All Users', icon: '👥' },
    { value: 'free', label: 'Free Users', icon: '🆓' },
    { value: 'vip', label: 'VIP Users', icon: '⭐' },
    { value: 'premium', label: 'Premium Users', icon: '💎' }
  ];

  const iconOptions = ['🔔', '📢', '⚠️', '✅', 'ℹ️', '🎉', '🚀', '💡', '📣', '⏰', '🔥', '🎯'];

  const pushIconOptions = ['📱', '🔔', '📢', '✅', 'ℹ️', '🎉', '🚀', '💡', '⚡', '💰', '🎓', '📊'];

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const response = await getAllNotificationsAdmin();
      setNotifications(response.data.notifications || []);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      showNotification('Error loading notifications', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 4000);
  };

  const handleOpenModal = (notif = null) => {
    if (notif) {
      setEditingNotification(notif);
      setFormData({
        title: notif.title || '',
        message: notif.message || '',
        type: notif.type || 'announcement',
        targetAudience: notif.targetAudience || 'all',
        icon: notif.icon || '🔔',
        actionUrl: notif.actionUrl || '',
        priority: notif.priority || 'medium',
        expiresAt: notif.expiresAt ? new Date(notif.expiresAt).toISOString().slice(0, 16) : ''
      });
    } else {
      setEditingNotification(null);
      setFormData({
        title: '',
        message: '',
        type: 'announcement',
        targetAudience: 'all',
        icon: '🔔',
        actionUrl: '',
        priority: 'medium',
        expiresAt: ''
      });
    }
    setFormErrors({});
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingNotification(null);
    setFormData({
      title: '',
      message: '',
      type: 'announcement',
      targetAudience: 'all',
      icon: '🔔',
      actionUrl: '',
      priority: 'medium',
      expiresAt: ''
    });
    setFormErrors({});
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.title.trim()) errors.title = 'Title is required';
    if (!formData.message.trim()) errors.message = 'Message is required';
    if (formData.title.length > 200) errors.title = 'Title must be less than 200 characters';
    if (formData.message.length > 1000) errors.message = 'Message must be less than 1000 characters';

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      showNotification('Please fix the form errors', 'error');
      return;
    }

    try {
      const payload = {
        ...formData,
        expiresAt: formData.expiresAt ? new Date(formData.expiresAt).toISOString() : null
      };

      if (editingNotification) {
        await updateNotification(editingNotification._id, payload);
        showNotification('Notification updated successfully!');
      } else {
        const response = await createNotification(payload);
        const recipientCount = response.data.recipientCount || 0;
        showNotification(`Notification sent to ${recipientCount} users!`);
      }

      handleCloseModal();
      fetchNotifications();
    } catch (error) {
      console.error('Error saving notification:', error);
      showNotification(error.response?.data?.message || 'Error saving notification', 'error');
    }
  };

  const handleDelete = async () => {
    if (!deletingNotification) return;

    try {
      await deleteNotification(deletingNotification._id);
      showNotification('Notification deleted successfully!');
      setShowDeleteConfirm(false);
      setDeletingNotification(null);
      fetchNotifications();
    } catch (error) {
      console.error('Error deleting notification:', error);
      showNotification(error.response?.data?.message || 'Error deleting notification', 'error');
    }
  };

  const openDeleteConfirm = (notif) => {
    setDeletingNotification(notif);
    setShowDeleteConfirm(true);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const getTypeColor = (type) => {
    const typeObj = notificationTypes.find(t => t.value === type);
    return typeObj ? typeObj.color : '#2196F3';
  };

  // Push Notification Functions
  const handleOpenPushModal = () => {
    setPushFormData({
      title: '',
      message: '',
      actionUrl: '',
      targetAudience: 'all',
      icon: '📱'
    });
    setPushFormErrors({});
    setShowPushModal(true);
  };

  const handleClosePushModal = () => {
    setShowPushModal(false);
    setPushFormData({
      title: '',
      message: '',
      actionUrl: '',
      targetAudience: 'all',
      icon: '📱'
    });
    setPushFormErrors({});
  };

  const validatePushForm = () => {
    const errors = {};
    if (!pushFormData.title.trim()) errors.title = 'Title is required';
    if (!pushFormData.message.trim()) errors.message = 'Message is required';
    if (pushFormData.title.length > 100) errors.title = 'Title must be less than 100 characters';
    if (pushFormData.message.length > 200) errors.message = 'Message must be less than 200 characters';

    setPushFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSendPushNotification = async (e) => {
    e.preventDefault();

    if (!validatePushForm()) {
      showNotification('Please fix the form errors', 'error');
      return;
    }

    try {
      setSendingPush(true);

      const response = await sendPushNotification(pushFormData);

      showNotification(`Push notification sent to ${response.data.sentCount || 0} users!`);
      handleClosePushModal();
      fetchNotifications(); // Refresh the list
    } catch (error) {
      console.error('Error sending push notification:', error);
      showNotification(error.response?.data?.message || error.message || 'Error sending push notification', 'error');
    } finally {
      setSendingPush(false);
    }
  };

  return (
    <div className="notification-panel">
      {/* Header */}
      <div className="notification-panel-header">
        <div>
          <h1><Bell size={32} /> Notification Management</h1>
          <p>Send notifications to users across the platform</p>
        </div>
        <div className="header-buttons">
          <button className="btn-primary" onClick={() => handleOpenModal()}>
            <Plus size={20} />
            Create Notification
          </button>
          <button className="btn-primary btn-push" onClick={handleOpenPushModal}>
            <Smartphone size={20} />
            Create Push Notification
          </button>
        </div>
      </div>

      {/* Notifications List */}
      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading notifications...</p>
        </div>
      ) : notifications.length === 0 ? (
        <div className="empty-state">
          <Bell size={48} color="#ccc" />
          <h3>No notifications yet</h3>
          <p>Create your first notification to get started</p>
        </div>
      ) : (
        <div>
          <p style={{ color: '#666', marginBottom: '1rem' }}>
            Total notifications: <strong>{notifications.length}</strong>
          </p>
          <div className="notifications-grid">
            {notifications.map(notif => (
              <div key={notif._id} className="notification-card">
                <div className="notification-card-header">
                  <div className="notification-icon" style={{ background: getTypeColor(notif.type) }}>
                    {notif.icon}
                  </div>
                  <div className="notification-actions">
                    <button className="btn-icon" onClick={() => handleOpenModal(notif)}>
                      <Edit2 size={18} />
                    </button>
                    <button className="btn-icon btn-danger" onClick={() => openDeleteConfirm(notif)}>
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>

                <div className="notification-card-body">
                  <h3>{notif.title}</h3>
                  <p className="notification-message">{notif.message}</p>

                  <div className="notification-meta">
                    {notif.isPushNotification && (
                      <span className="notification-badge notification-type-badge">
                        <Smartphone size={14} />
                        Push
                      </span>
                    )}
                    <span
                      className="notification-badge"
                      style={{ background: getTypeColor(notif.type), color: 'white' }}
                    >
                      {notif.type}
                    </span>
                    <span className="notification-badge notification-badge-audience">
                      <Users size={14} />
                      {notif.targetAudience}
                    </span>
                    {notif.priority === 'high' && (
                      <span className="notification-badge notification-badge-priority">
                        High Priority
                      </span>
                    )}
                  </div>

                  <div className="notification-stats">
                    <div className="stat">
                      <Eye size={16} />
                      <span>{notif.readCount || 0} reads</span>
                    </div>
                    <div className="stat">
                      <span className="notification-date">{formatDate(notif.createdAt)}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content notification-modal" onClick={(e) => e.stopPropagation()}>
            {/* Fixed Header */}
            <div className="modal-header">
              <h2>
                {editingNotification ? 'Edit Notification' : 'Create New Notification'}
              </h2>
              <button className="btn-icon" onClick={handleCloseModal}>
                <X size={24} />
              </button>
            </div>

            {/* Scrollable Content - Form INSIDE */}
            <div className="modal-body-scrollable">
              <form onSubmit={handleSubmit}>
                {/* Title */}
                <div className="form-group">
                  <label>Title *</label>
                  <input
                    type="text"
                    className={formErrors.title ? 'error' : ''}
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="e.g., New Trading Indicators Available!"
                    maxLength={200}
                  />
                  {formErrors.title && <span className="error-text">{formErrors.title}</span>}
                  <small>{formData.title.length}/200 characters</small>
                </div>

                {/* Message */}
                <div className="form-group">
                  <label>Message *</label>
                  <textarea
                    className={formErrors.message ? 'error' : ''}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder="Enter your notification message..."
                    rows={4}
                    maxLength={1000}
                  />
                  {formErrors.message && <span className="error-text">{formErrors.message}</span>}
                  <small>{formData.message.length}/1000 characters</small>
                </div>

                <div className="form-row">
                  {/* Type */}
                  <div className="form-group">
                    <label>Type</label>
                    <select
                      value={formData.type}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    >
                      {notificationTypes.map(type => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Target Audience */}
                  <div className="form-group">
                    <label>Target Audience</label>
                    <select
                      value={formData.targetAudience}
                      onChange={(e) => setFormData({ ...formData, targetAudience: e.target.value })}
                    >
                      {targetAudiences.map(audience => (
                        <option key={audience.value} value={audience.value}>
                          {audience.icon} {audience.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  {/* Icon */}
                  <div className="form-group">
                    <label>Icon</label>
                    <div className="icon-selector">
                      {iconOptions.map(icon => (
                        <button
                          key={icon}
                          type="button"
                          className={`icon-option ${formData.icon === icon ? 'selected' : ''}`}
                          onClick={() => setFormData({ ...formData, icon })}
                        >
                          {icon}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Priority */}
                  <div className="form-group">
                    <label>Priority</label>
                    <select
                      value={formData.priority}
                      onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                    </select>
                  </div>
                </div>

                {/* Action URL */}
                <div className="form-group">
                  <label>Action URL (Optional)</label>
                  <input
                    type="text"
                    value={formData.actionUrl}
                    onChange={(e) => setFormData({ ...formData, actionUrl: e.target.value })}
                    placeholder="/coder/templates or https://example.com"
                  />
                  <small>URL users will be redirected to when clicking the notification</small>
                </div>

                {/* Expires At */}
                <div className="form-group">
                  <label>Expires At (Optional)</label>
                  <input
                    type="datetime-local"
                    value={formData.expiresAt}
                    onChange={(e) => setFormData({ ...formData, expiresAt: e.target.value })}
                  />
                  <small>Leave empty for permanent notification</small>
                </div>

                {/* Form Actions - Inside Scrollable Area */}
                <div className="form-actions">
                  <button type="button" className="btn-secondary" onClick={handleCloseModal}>
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary">
                    <Send size={20} />
                    {editingNotification ? 'Update Notification' : 'Send Notification'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="modal-content modal-small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Delete Notification</h2>
              <button className="btn-icon" onClick={() => setShowDeleteConfirm(false)}>
                <X size={24} />
              </button>
            </div>

            <div className="modal-body">
              <div className="delete-confirm-content">
                <AlertCircle size={48} color="#f44336" />
                <p>Are you sure you want to delete this notification?</p>
                <p className="delete-warning">This action cannot be undone.</p>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowDeleteConfirm(false)}>
                Cancel
              </button>
              <button className="btn-danger" onClick={handleDelete}>
                <Trash2 size={20} />
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Push Notification Modal */}
      {showPushModal && (
        <div className="modal-overlay" onClick={handleClosePushModal}>
          <div className="modal-content notification-modal push-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <Smartphone size={24} style={{ marginRight: '8px' }} />
                Create Push Notification
              </h2>
              <button className="btn-icon" onClick={handleClosePushModal}>
                <X size={24} />
              </button>
            </div>

            <div className="modal-body-scrollable">
              <form onSubmit={handleSendPushNotification}>
                <div className="push-notification-info">
                  <div className="info-box">
                    <Smartphone size={20} />
                    <span>Push notifications are sent directly to users' devices via FCM</span>
                  </div>
                </div>

                {/* Title */}
                <div className="form-group">
                  <label>Title *</label>
                  <input
                    type="text"
                    className={pushFormErrors.title ? 'error' : ''}
                    value={pushFormData.title}
                    onChange={(e) => setPushFormData({ ...pushFormData, title: e.target.value })}
                    placeholder="e.g., New Course Available!"
                    maxLength={100}
                  />
                  {pushFormErrors.title && <span className="error-text">{pushFormErrors.title}</span>}
                  <small>{pushFormData.title.length}/100 characters</small>
                </div>

                {/* Message */}
                <div className="form-group">
                  <label>Message *</label>
                  <textarea
                    className={pushFormErrors.message ? 'error' : ''}
                    value={pushFormData.message}
                    onChange={(e) => setPushFormData({ ...pushFormData, message: e.target.value })}
                    placeholder="Enter your push notification message..."
                    rows={3}
                    maxLength={200}
                  />
                  {pushFormErrors.message && <span className="error-text">{pushFormErrors.message}</span>}
                  <small>{pushFormData.message.length}/200 characters (keep it short for mobile)</small>
                </div>

                {/* Target Audience */}
                <div className="form-group">
                  <label>Target Audience</label>
                  <select
                    value={pushFormData.targetAudience}
                    onChange={(e) => setPushFormData({ ...pushFormData, targetAudience: e.target.value })}
                  >
                    {targetAudiences.map(audience => (
                      <option key={audience.value} value={audience.value}>
                        {audience.icon} {audience.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Icon */}
                <div className="form-group">
                  <label>Notification Icon</label>
                  <div className="icon-selector">
                    {pushIconOptions.map(icon => (
                      <button
                        key={icon}
                        type="button"
                        className={`icon-option ${pushFormData.icon === icon ? 'selected' : ''}`}
                        onClick={() => setPushFormData({ ...pushFormData, icon })}
                      >
                        {icon}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Action URL */}
                <div className="form-group">
                  <label>Action Link (Optional)</label>
                  <input
                    type="text"
                    value={pushFormData.actionUrl}
                    onChange={(e) => setPushFormData({ ...pushFormData, actionUrl: e.target.value })}
                    placeholder="/courses/123 or /dashboard"
                  />
                  <small>Where users go when they tap the notification</small>
                </div>

                {/* Preview */}
                <div className="form-group">
                  <label>Preview</label>
                  <div className="push-notification-preview">
                    <div className="preview-device">
                      <div className="preview-notification">
                        <div className="preview-icon">{pushFormData.icon}</div>
                        <div className="preview-content">
                          <div className="preview-title">
                            {pushFormData.title || 'Notification Title'}
                          </div>
                          <div className="preview-message">
                            {pushFormData.message || 'Notification message will appear here'}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Form Actions */}
                <div className="form-actions">
                  <button type="button" className="btn-secondary" onClick={handleClosePushModal}>
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary" disabled={sendingPush}>
                    <Smartphone size={20} />
                    {sendingPush ? 'Sending...' : 'Send Push Notification'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {notification.show && (
        <div className={`toast-notification toast-${notification.type}`}>
          {notification.message}
        </div>
      )}
    </div>
  );
};

export default NotificationPanel;